import React, { useState, useEffect, useCallback } from "react";

import { Box, Typography, Button, Chip, CircularProgress, IconButton, Tooltip } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import WarningIcon from "@material-ui/icons/Warning";
import ErrorIcon from "@material-ui/icons/Error";
import InfoIcon from "@material-ui/icons/Info";
import HelpOutlineIcon from "@material-ui/icons/HelpOutline";
import RefreshIcon from "@material-ui/icons/Refresh";
import OpenInNewIcon from "@material-ui/icons/OpenInNew";
import DescriptionIcon from "@material-ui/icons/Description";
import ReplayIcon from "@material-ui/icons/Replay";
import { toast } from "react-toastify";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";

const PALETTE = {
  ok: { bg: "#f0fdf4", border: "#22c55e", text: "#166534", icon: "#22c55e" },
  info: { bg: "#f3f4f6", border: "#9ca3af", text: "#4b5563", icon: "#9ca3af" },
  warning: { bg: "#fffbeb", border: "#f59e0b", text: "#92400e", icon: "#f59e0b" },
  error: { bg: "#fef2f2", border: "#ef4444", text: "#991b1b", icon: "#ef4444" },
  neutral: { bg: "#f3f4f6", border: "#9ca3af", text: "#4b5563", icon: "#9ca3af" },
};

const QUALITY_COLOR = { GREEN: "#22c55e", YELLOW: "#f59e0b", RED: "#ef4444" };

const useStyles = makeStyles((theme) => ({
  wrapper: { display: "flex", flexDirection: "column", gap: 6 },
  headerRow: { display: "flex", justifyContent: "flex-end" },
  row: {
    display: "flex",
    alignItems: "flex-start",
    gap: 10,
    padding: "8px 12px",
    borderRadius: 8,
    flexWrap: "wrap",
  },
  blockingRow: {
    boxShadow: "inset 0 0 0 1px rgba(239,68,68,0.35)",
  },
  text: { flex: 1, minWidth: 180 },
  titleRow: { display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" },
  title: { fontWeight: 700, fontSize: 13, lineHeight: 1.3 },
  desc: { fontSize: 12, lineHeight: 1.35, marginTop: 2 },
  note: { fontSize: 11, lineHeight: 1.3, marginTop: 3, fontStyle: "italic", opacity: 0.85 },
  actions: { display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" },
  blockingChip: {
    fontSize: 10,
    fontWeight: 700,
    height: 20,
    background: "#ef4444",
    color: "#fff",
  },
}));

// feat_219: nivel -> ícono. check_failed usa "neutral" (gris, igual que antes)
// aunque su level técnico sea "warning" — es un estado "no sabemos", no un
// problema real detectado.
const iconFor = (level, code) => {
  if (code === "check_failed") return HelpOutlineIcon;
  if (level === "ok") return CheckCircleIcon;
  if (level === "error") return ErrorIcon;
  if (level === "info") return InfoIcon;
  return WarningIcon;
};

const paletteFor = (level, code) => {
  if (code === "check_failed") return PALETTE.neutral;
  return PALETTE[level] || PALETTE.warning;
};

// feat_201/203/204/219 — cartel de estado por conexión Meta. Consulta
// GET /whatsapp/:id/meta-health (billing/pending/name/quality/templates).
// Muestra TODOS los problemas detectados a la vez (issues[]), no solo el
// primero — un número puede tener varios simultáneamente (ej. sin pago
// configurado Y con el nombre rechazado). Los bloqueantes (impiden enviar)
// van arriba, destacados; los no-bloqueantes abajo, con nota aclaratoria.
// onCreateTemplate: abre el drawer de plantillas interno de WAIA (no_templates).
const MetaHealthBanner = ({ whatsApp, onCreateTemplate }) => {
  const classes = useStyles();
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(true);
  const [registering, setRegistering] = useState(false);

  const fetchHealth = useCallback(
    async (force = false) => {
      setLoading(true);
      try {
        const { data } = await api.get(`/whatsapp/${whatsApp.id}/meta-health`, {
          params: force ? { force: "true" } : {},
        });
        setHealth(data);
      } catch {
        setHealth({
          applicable: true,
          overall: "warning",
          issues: [{ code: "check_failed", level: "warning", blocking: false, actionType: "none", actionUrl: null }],
        });
      }
      setLoading(false);
    },
    [whatsApp.id]
  );

  useEffect(() => {
    fetchHealth(false);
  }, [fetchHealth]);

  // feat_209: acción WAIA (NO link a Meta) para el estado register_needed → dispara
  // el register de Cloud API internamente y refresca TODOS los issues.
  const handleRegister = useCallback(async () => {
    setRegistering(true);
    try {
      const { data } = await api.post(`/whatsapp/${whatsApp.id}/meta-register`);
      if (data?.ok) {
        toast.success(i18n.t("connections.metaHealth.register_needed.retryOk"));
      } else {
        toast.error(i18n.t("connections.metaHealth.register_needed.retryFail"));
      }
    } catch {
      toast.error(i18n.t("connections.metaHealth.register_needed.retryFail"));
    }
    await fetchHealth(true);
    setRegistering(false);
  }, [whatsApp.id, fetchHealth]);

  if (loading && !health) {
    return (
      <Box className={classes.row} style={{ background: PALETTE.neutral.bg }}>
        <CircularProgress size={16} />
        <Typography className={classes.desc} style={{ color: PALETTE.neutral.text }}>
          {i18n.t("connections.metaHealth.checking")}
        </Typography>
      </Box>
    );
  }

  if (!health || health.applicable === false) return null;

  // Compat defensiva: si por algún motivo llegara el shape viejo (un solo
  // estado), lo envolvemos en un array de 1 issue para no romper el render.
  const issues =
    Array.isArray(health.issues) && health.issues.length > 0
      ? health.issues
      : [
          {
            code: health.code || "check_failed",
            level: health.level || "warning",
            blocking: false,
            actionType: health.actionType || "none",
            actionUrl: health.actionUrl || null,
          },
        ];

  const quality = health.data?.qualityRating;

  return (
    <div className={classes.wrapper}>
      <div className={classes.headerRow}>
        <Tooltip title={i18n.t("connections.metaHealth.refresh")}>
          <span>
            <IconButton size="small" onClick={() => fetchHealth(true)} disabled={loading}>
              {loading ? <CircularProgress size={16} /> : <RefreshIcon style={{ fontSize: 18 }} />}
            </IconButton>
          </span>
        </Tooltip>
      </div>

      {issues.map((issue) => {
        const { code, level, blocking, actionType, actionUrl, descKey } = issue;
        const p = paletteFor(level, code);
        const Icon = iconFor(level, code);

        return (
          <Box
            key={code}
            className={`${classes.row} ${blocking ? classes.blockingRow : ""}`}
            style={{ background: p.bg, borderLeft: `4px solid ${p.border}` }}
          >
            <Icon style={{ color: p.icon, fontSize: 20, marginTop: 1 }} />
            <div className={classes.text}>
              <div className={classes.titleRow}>
                <Typography className={classes.title} style={{ color: p.text }}>
                  {i18n.t(`connections.metaHealth.${code}.title`)}
                </Typography>
                {blocking && (
                  <Chip
                    size="small"
                    label={i18n.t("connections.metaHealth.blockingChip")}
                    className={classes.blockingChip}
                  />
                )}
              </div>
              <Typography className={classes.desc} style={{ color: p.text }}>
                {i18n.t(descKey || `connections.metaHealth.${code}.desc`)}
              </Typography>
              {code !== "ok" && code !== "check_failed" && (
                <Typography className={classes.note} style={{ color: p.text }}>
                  {blocking
                    ? i18n.t("connections.metaHealth.blockingNote")
                    : i18n.t("connections.metaHealth.nonBlockingNote")}
                </Typography>
              )}
            </div>

            <div className={classes.actions}>
              {/* quality_low: chip visible con el nivel de calidad reportado por Meta */}
              {code === "quality_low" && quality && (
                <Chip
                  size="small"
                  label={i18n.t("connections.metaHealth.qualityChip", {
                    quality: i18n.t(`connections.metaHealth.quality.${quality}`),
                  })}
                  style={{ background: QUALITY_COLOR[quality] || "#9ca3af", color: "#fff", fontWeight: 700 }}
                />
              )}

              {/* register_needed: acción INTERNA WAIA → dispara el register de Cloud API */}
              {actionType === "waia_register" && (
                <Button
                  size="small"
                  variant="contained"
                  startIcon={registering ? <CircularProgress size={15} style={{ color: "#fff" }} /> : <ReplayIcon style={{ fontSize: 15 }} />}
                  style={{ background: p.border, color: "#fff", whiteSpace: "nowrap" }}
                  onClick={handleRegister}
                  disabled={registering}
                >
                  {registering
                    ? i18n.t("connections.metaHealth.register_needed.retrying")
                    : i18n.t("connections.metaHealth.register_needed.action")}
                </Button>
              )}

              {/* no_templates: acción INTERNA → abre el drawer de plantillas de WAIA */}
              {actionType === "internal_template" && onCreateTemplate && (
                <Button
                  size="small"
                  variant="contained"
                  startIcon={<DescriptionIcon style={{ fontSize: 15 }} />}
                  style={{ background: p.border, color: "#fff", whiteSpace: "nowrap" }}
                  onClick={onCreateTemplate}
                >
                  {i18n.t("connections.metaHealth.no_templates.action")}
                </Button>
              )}

              {/* meta_link (billing_missing / name_declined): abre Meta en pestaña nueva */}
              {actionType === "meta_link" && actionUrl && (
                <Button
                  size="small"
                  variant="contained"
                  endIcon={<OpenInNewIcon style={{ fontSize: 14 }} />}
                  style={{ background: p.border, color: "#fff", whiteSpace: "nowrap" }}
                  onClick={() => window.open(actionUrl, "_blank", "noopener,noreferrer")}
                >
                  {i18n.t(`connections.metaHealth.${code}.action`)}
                </Button>
              )}
            </div>
          </Box>
        );
      })}
    </div>
  );
};

export default MetaHealthBanner;
